import { VALUE_FORMAT_TYPE } from "@/constants";
import {
  datePartsFromLocalTimestamp,
  datePartsToLocalTimestamp,
  formatDateParts,
  parseDateValue,
} from "@/shared/lib/datePattern";
import {
  formatValue,
  parseValueFormat,
  valueFormatsEqual,
  type ValueFormat,
} from "@/shared/lib/valueFormat";

/**
 * Display formats on a chart, applied the way ECharts takes them: as label and tooltip formatters.
 * The series data stays raw, so a format never changes where a point is drawn -- with one
 * exception, a time axis whose dates arrive as text only a pattern can read (see
 * `dimensionAxisFormatting`).
 */

/** The ECharts axis kinds a dimension can be drawn on. */
export type ChartDimensionAxisKind = "time" | "category" | "value";

/** A field's display format, or null when it has none or what it has is malformed. */
export function chartFieldFormat(
  field: { format?: unknown } | null | undefined,
): ValueFormat | null {
  return parseValueFormat(field?.format);
}

/** `value` as `format` shows it, or as plain text when there is no format or it does not apply. */
export function formatChartValue(value: unknown, format: ValueFormat | null): string {
  if (value === null || value === undefined) {
    return "";
  }
  return formatValue(value, format) ?? String(value);
}

/**
 * The format a value axis labels its ticks with: the one every series on the axis shares. Series
 * formatted differently share no tick label, so the axis keeps plain numbers and each series' own
 * format still shows in its tooltip.
 */
export function sharedChartFormat(
  fields: readonly ({ format?: unknown } | null | undefined)[],
): ValueFormat | null {
  const formats = fields.map(chartFieldFormat);
  const [first] = formats;
  if (first === undefined || first === null) {
    return null;
  }
  return formats.every((format) => valueFormatsEqual(format, first)) ? first : null;
}

/** ECharts `axisLabel.formatter` for a value axis, or nothing to leave its numbers plain. */
export function valueAxisLabelFormatter(
  format: ValueFormat | null,
): { formatter: (value: number) => string } | Record<string, never> {
  return format === null ? {} : { formatter: (value: number) => formatChartValue(value, format) };
}

export type DimensionAxisFormatting = {
  /** What the axis plots for a raw value: unchanged, except dates read through a pattern. */
  toAxisValue: (raw: unknown) => unknown;
  /** The text for a tick or the tooltip header, given the value the axis plotted. */
  formatLabel: (value: unknown) => string;
};

/**
 * How a dimension's format reaches its axis, or null when it has none that applies there.
 *
 * A time axis places points by instant, so a date it cannot parse on its own -- `15/01/2024` --
 * is read here with the format's input pattern and handed over as a timestamp, then written back
 * out with the output pattern. Both directions go through the browser's zone so the wall clock
 * the data says is the one shown. A number format means nothing on a time axis and is ignored.
 */
export function dimensionAxisFormatting(
  format: ValueFormat | null,
  axisKind: ChartDimensionAxisKind,
): DimensionAxisFormatting | null {
  if (format === null) {
    return null;
  }
  if (axisKind === "time") {
    if (format.type !== VALUE_FORMAT_TYPE.DATE) {
      return null;
    }
    return {
      toAxisValue: (raw) => {
        const parts = parseDateValue(raw, format.input_pattern);
        return parts === null ? raw : datePartsToLocalTimestamp(parts);
      },
      formatLabel: (value) => {
        const parts = typeof value === "number" ? datePartsFromLocalTimestamp(value) : null;
        return parts === null
          ? formatChartValue(value, format)
          : formatDateParts(parts, format.output_pattern);
      },
    };
  }
  return {
    toAxisValue: (raw) => raw,
    formatLabel: (value) => formatChartValue(value, format),
  };
}

/**
 * The options that put a dimension's format on its axis: the tick labels, and -- through the
 * axis pointer's label, which ECharts also uses for an axis tooltip's header -- the tooltip.
 */
export function dimensionAxisLabelOptions(formatting: DimensionAxisFormatting | null) {
  if (formatting === null) {
    return { axisLabel: {}, axisPointer: undefined };
  }
  return {
    axisLabel: { formatter: (value: string | number) => formatting.formatLabel(value) },
    axisPointer: {
      label: { formatter: (params: { value: unknown }) => formatting.formatLabel(params.value) },
    },
  };
}

/** What ECharts' own tooltip shows for a value: numbers with thousands separators, "-" for none. */
const DEFAULT_TOOLTIP_NUMBER_FORMAT: ValueFormat = {
  type: VALUE_FORMAT_TYPE.NUMBER,
  thousands_separator: true,
};

function defaultTooltipValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "-";
  }
  return formatChartValue(value, DEFAULT_TOOLTIP_NUMBER_FORMAT);
}

/**
 * A series' tooltip `valueFormatter` for its measure's format, or nothing when it has none -- which
 * leaves ECharts' own formatting in place. A point that carries its x as well (`[x, y]`, a scatter
 * point) has the x shown through the dimension's format.
 */
export function seriesTooltipOption(
  measureFormat: ValueFormat | null,
  dimension: DimensionAxisFormatting | null = null,
) {
  if (measureFormat === null) {
    return {};
  }
  const formatMeasure = (value: unknown) =>
    value === null || value === undefined ? "-" : formatChartValue(value, measureFormat);
  return {
    tooltip: {
      valueFormatter: (value: unknown): string =>
        Array.isArray(value)
          ? value
              .map((entry: unknown, index) =>
                index === value.length - 1
                  ? formatMeasure(entry)
                  : (dimension?.formatLabel(entry) ?? defaultTooltipValue(entry)),
              )
              .join(", ")
          : formatMeasure(value),
    },
  };
}
